import React, { useState, useEffect } from 'react';
import { Separator } from '@/components/ui/separator';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Brain, Plus, Trash2, Loader2, Settings, X, ChevronDown, ChevronRight,
  TrendingUp, TrendingDown, Minus, AlertTriangle, Lightbulb, Activity, Target,
  Zap, ShieldCheck, Wand2
} from 'lucide-react';
import { llmConfigAPI, systemAPI, routingAPI } from '../api';

// ---- Generic List Renderer ----
function GenericList({ title, items }) {
  const formatted = title.replace(/_/g, ' ');
  return (
    <div className="mb-6">
      <h3 className="text-sm font-bold text-[#111827] capitalize mb-3" style={{ fontFamily: 'var(--font-heading)' }}>{formatted} ({items.length})</h3>
      <div className="space-y-2">
        {items.map((item, i) => {
          if (typeof item === 'string') {
            return <div key={`${title}-str-${i}`} className="p-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg text-xs text-[#4B5563]">{item}</div>;
          }
          return (
            <div key={item.id || item.title || `${title}-${i}`} className="p-3 border border-[#E5E7EB] rounded-lg">
              {item.title && <p className="text-xs font-semibold text-[#111827] mb-1">{item.title || item.name}</p>}
              {item.description && <p className="text-xs text-[#4B5563]">{item.description}</p>}
              {item.content && <p className="text-xs text-[#4B5563]">{item.content}</p>}
              {item.status && <span className="text-[10px] text-[#6B7280] mt-1 inline-block">{item.status}</span>}
              {item.priority && <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded ${item.priority === 'high' ? 'bg-[#FEE2E2] text-[#DC2626]' : item.priority === 'medium' ? 'bg-[#FEF3C7] text-[#92400E]' : 'bg-[#F3F4F6] text-[#4B5563]'}`}>{item.priority}</span>}
              {item.question && <p className="text-xs text-[#111827] font-medium">Q: {item.question}</p>}
              {item.answer && <p className="text-xs text-[#4B5563] mt-0.5">A: {item.answer}</p>}
              {item.front && <p className="text-xs text-[#6590FF] font-medium">Q: {item.front}</p>}
              {item.back && <p className="text-xs text-[#4B5563] mt-0.5">A: {item.back}</p>}
              {item.action && <p className="text-xs text-[#6590FF] font-medium">{item.action}</p>}
              {item.deadline && <p className="text-[10px] text-[#9CA3AF] mt-0.5">Due: {item.deadline}</p>}
              {item.impact && <p className="text-xs text-[#4B5563] mt-0.5">Impact: {item.impact}</p>}
              {item.source && <p className="text-[10px] text-[#9CA3AF] mt-0.5">Source: {item.source}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---- Fullscreen Tool Result Renderer ----
export function FullscreenToolResult({ data }) {
  const result = data?.result || {};
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const isGovGraph = data?.tool_id === 'gov_graph' && result.nodes;
  const isTrend = data?.tool_id === 'trend_analyzer' && result.trends;

  if (isGovGraph) {
    const GovGraphViewer = React.lazy(() => import('./GovGraphViewer'));
    return (
      <React.Suspense fallback={<div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#6590FF]" /></div>}>
        <GovGraphViewer data={result} />
      </React.Suspense>
    );
  }

  if (isTrend) {
    return <TrendResultView result={result} />;
  }

  return (
    <div className="max-w-5xl mx-auto p-8" data-testid="fullscreen-tool-result">
      <h2 className="text-xl font-bold text-[#111827] mb-4 capitalize" style={{ fontFamily: 'var(--font-heading)' }}>
        {data.tool_id?.replace(/_/g, ' ') || 'Tool Result'}
      </h2>
      <ToolResultMedia result={result} apiUrl={API_URL} />
      <ToolResultContent result={result} />
    </div>
  );
}

// ---- Trend Result View ----
function TrendResultView({ result }) {
  const [activeTab, setActiveTab] = useState('overview');
  const trends = result.trends || [];
  const chartData = result.trend_chart_data || trends.map(t => ({ label: t.name, value: t.strength, direction: t.direction, category: t.category }));
  const categories = result.category_breakdown || [];
  const maxVal = Math.max(...chartData.map(d => d.value || 0), 100);

  const dirIcon = (dir) => {
    if (dir === 'up' || dir === 'emerging') return <TrendingUp className="w-4 h-4 text-[#059669]" />;
    if (dir === 'down' || dir === 'declining') return <TrendingDown className="w-4 h-4 text-[#DC2626]" />;
    return <Minus className="w-4 h-4 text-[#6B7280]" />;
  };
  const dirColor = (dir) => {
    if (dir === 'up' || dir === 'emerging') return '#059669';
    if (dir === 'down' || dir === 'declining') return '#DC2626';
    return '#6B7280';
  };
  const impactColor = (lvl) => {
    if (lvl === 'critical') return 'bg-[#DC2626]/10 text-[#DC2626]';
    if (lvl === 'high') return 'bg-[#D97706]/10 text-[#D97706]';
    if (lvl === 'medium') return 'bg-[#6590FF]/10 text-[#6590FF]';
    return 'bg-[#6B7280]/10 text-[#6B7280]';
  };

  return (
    <div className="max-w-6xl mx-auto p-8" data-testid="trend-result-view">
      {/* Summary Banner */}
      <div className="mb-6 p-5 bg-gradient-to-r from-[#FEF3C7] to-[#FFFBEB] border border-[#FDE68A] rounded-2xl">
        <div className="flex items-start gap-3">
          <Activity className="w-6 h-6 text-[#D97706] flex-shrink-0 mt-0.5" />
          <div>
            <h2 className="text-lg font-bold text-[#111827] mb-1" style={{ fontFamily: 'var(--font-heading)' }}>Trend Analysis</h2>
            <p className="text-sm text-[#4B5563] leading-relaxed">{result.summary}</p>
            <div className="flex items-center gap-4 mt-3">
              <span className="text-xs font-semibold text-[#D97706] bg-white/60 px-2.5 py-1 rounded-lg">{result.total_trends_found || trends.length} trends found</span>
              {categories.length > 0 && <span className="text-xs text-[#92400E]">{categories.length} categories</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="flex gap-1 border-b border-[#E5E7EB] mb-6" data-testid="trend-tabs">
        {[{key:'overview',label:'Overview'},{key:'trends',label:`Trends (${trends.length})`},{key:'charts',label:'Charts'},{key:'signals',label:'Signals'}].map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${activeTab === t.key ? 'text-[#D97706] border-[#D97706]' : 'text-[#6B7280] border-transparent hover:text-[#111827]'}`}
            data-testid={`trend-tab-${t.key}`}>{t.label}</button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Bar Chart */}
          <div className="p-5 bg-white border border-[#E5E7EB] rounded-2xl">
            <h3 className="text-sm font-bold text-[#111827] mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Trend Strength</h3>
            <div className="space-y-3">
              {chartData.slice(0, 10).map((d, i) => (
                <div key={d.label || `chart-${i}`} className="flex items-center gap-3">
                  <span className="text-xs font-medium text-[#4B5563] w-32 truncate text-right">{d.label}</span>
                  <div className="flex-1 h-7 bg-[#F3F4F6] rounded-full overflow-hidden relative">
                    <div className="h-full rounded-full transition-all duration-700 flex items-center justify-end pr-2"
                      style={{ width: `${Math.max((d.value / maxVal) * 100, 8)}%`, backgroundColor: dirColor(d.direction), opacity: 0.85 }}>
                      <span className="text-[10px] font-bold text-white">{d.value}</span>
                    </div>
                  </div>
                  {dirIcon(d.direction)}
                </div>
              ))}
            </div>
          </div>

          {/* Category Breakdown Donut-like summary */}
          {categories.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {categories.map((c, i) => (
                <div key={c.category || `cat-${i}`} className="p-4 bg-white border border-[#E5E7EB] rounded-2xl text-center">
                  <p className="text-2xl font-bold text-[#111827]">{c.count}</p>
                  <p className="text-xs text-[#6B7280] mt-0.5">{c.category}</p>
                  <div className="mt-2 h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden">
                    <div className="h-full bg-[#D97706] rounded-full" style={{ width: `${Math.min(c.avg_strength || 50, 100)}%` }} />
                  </div>
                  <p className="text-[10px] text-[#9CA3AF] mt-1">Avg: {c.avg_strength || 0}</p>
                </div>
              ))}
            </div>
          )}

          {/* Key Takeaways */}
          {result.key_takeaways?.length > 0 && (
            <div className="p-5 bg-[#F0FDF4] border border-[#BBF7D0] rounded-2xl">
              <h3 className="text-sm font-bold text-[#111827] mb-3 flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
                <Target className="w-4 h-4 text-[#059669]" /> Key Takeaways
              </h3>
              <div className="space-y-2">
                {result.key_takeaways.map((t, i) => (
                  <div key={`takeaway-${i}`} className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-[#059669] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                    <p className="text-sm text-[#374151] leading-relaxed">{t}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Trends List Tab */}
      {activeTab === 'trends' && (
        <div className="space-y-3">
          {trends.map((t, i) => (
            <TrendCard key={t.name || t.trend || `trend-${i}`} trend={t} dirIcon={dirIcon} impactColor={impactColor} />
          ))}
        </div>
      )}

      {/* Charts Tab */}
      {activeTab === 'charts' && (
        <div className="space-y-6">
          <div className="p-5 bg-white border border-[#E5E7EB] rounded-2xl">
            <h3 className="text-sm font-bold text-[#111827] mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Strength Distribution</h3>
            <div className="flex items-end gap-2 h-48">
              {chartData.map((d, i) => (
                <div key={d.label || `bar-${i}`} className="flex-1 flex flex-col items-center gap-1" title={`${d.label}: ${d.value}`}>
                  <span className="text-[10px] font-bold text-[#4B5563]">{d.value}</span>
                  <div className="w-full rounded-t-lg transition-all duration-500"
                    style={{ height: `${Math.max((d.value / maxVal) * 160, 8)}px`, backgroundColor: dirColor(d.direction), opacity: 0.8 }} />
                  <span className="text-[9px] text-[#9CA3AF] truncate w-full text-center">{d.label?.split(' ')[0]}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Direction breakdown */}
          <div className="grid grid-cols-3 gap-3">
            {['up', 'down', 'stable'].map(dir => {
              const count = trends.filter(t => (t.direction === dir || (dir === 'up' && t.direction === 'emerging') || (dir === 'down' && t.direction === 'declining'))).length;
              return (
                <div key={dir} className="p-4 bg-white border border-[#E5E7EB] rounded-2xl flex items-center gap-3">
                  {dirIcon(dir)}
                  <div>
                    <p className="text-lg font-bold text-[#111827]">{count}</p>
                    <p className="text-xs text-[#6B7280] capitalize">{dir === 'up' ? 'Rising' : dir === 'down' ? 'Declining' : 'Stable'}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Signals Tab */}
      {activeTab === 'signals' && (
        <div className="space-y-6">
          {result.risk_signals?.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-[#111827] mb-3 flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
                <AlertTriangle className="w-4 h-4 text-[#DC2626]" /> Risk Signals
              </h3>
              <div className="space-y-2">
                {result.risk_signals.map((s, i) => (
                  <div key={`wm-1-${i}`} className="p-4 bg-white border border-[#E5E7EB] rounded-2xl">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${s.severity === 'critical' ? 'bg-[#DC2626]/10 text-[#DC2626]' : s.severity === 'warning' ? 'bg-[#D97706]/10 text-[#D97706]' : 'bg-[#6B7280]/10 text-[#6B7280]'}`}>{s.severity}</span>
                    </div>
                    <p className="text-sm text-[#111827] font-medium">{s.signal}</p>
                    {s.recommended_action && <p className="text-xs text-[#059669] mt-1.5">Action: {s.recommended_action}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.opportunities?.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-[#111827] mb-3 flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
                <Lightbulb className="w-4 h-4 text-[#059669]" /> Opportunities
              </h3>
              <div className="space-y-2">
                {result.opportunities.map((o, i) => (
                  <div key={`wm-2-${i}`} className="p-4 bg-white border border-[#E5E7EB] rounded-2xl">
                    <p className="text-sm text-[#111827] font-medium">{o.opportunity}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-[#059669]/10 text-[#059669]">Value: {o.potential_value}</span>
                      {o.action && <span className="text-xs text-[#4B5563]">{o.action}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.emerging_patterns?.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-[#111827] mb-3" style={{ fontFamily: 'var(--font-heading)' }}>Emerging Patterns</h3>
              <div className="space-y-2">
                {result.emerging_patterns.map((p, i) => (
                  <div key={`wm-3-${i}`} className="p-4 bg-[#F8FAFC] border border-[#E5E7EB] rounded-2xl">
                    <p className="text-sm text-[#111827]">{p.pattern}</p>
                    <span className={`text-[10px] font-bold uppercase mt-2 inline-block px-2 py-0.5 rounded-full ${impactColor(p.significance)}`}>{p.significance}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TrendCard({ trend, dirIcon, impactColor }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="p-4 bg-white border border-[#E5E7EB] rounded-2xl hover:border-[#D97706]/30 transition-colors" data-testid={`trend-card-${trend.id}`}>
      <div className="flex items-start gap-3">
        <div className="mt-0.5">{dirIcon(trend.direction)}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h4 className="text-sm font-bold text-[#111827]">{trend.name}</h4>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F3F4F6] text-[#4B5563] font-medium">{trend.category}</span>
            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${impactColor(trend.impact_level)}`}>{trend.impact_level}</span>
          </div>
          <p className="text-sm text-[#4B5563] leading-relaxed">{trend.description}</p>
          <div className="flex items-center gap-3 mt-2">
            <span className="text-[10px] text-[#9CA3AF]">Strength: <b className="text-[#111827]">{trend.strength}/100</b></span>
            <span className="text-[10px] text-[#9CA3AF]">Confidence: <b className="text-[#111827]">{trend.confidence}</b></span>
            <span className="text-[10px] text-[#9CA3AF] capitalize">{trend.timeframe}</span>
          </div>
          {trend.supporting_evidence?.length > 0 && (
            <button onClick={() => setExpanded(!expanded)} className="text-[11px] text-[#6590FF] font-medium mt-2 hover:underline flex items-center gap-1">
              {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              {trend.supporting_evidence.length} evidence{trend.supporting_evidence.length > 1 ? 's' : ''}
            </button>
          )}
          {expanded && trend.supporting_evidence?.map((e, i) => (
            <p key={`wm-4-${i}`} className="text-xs text-[#6B7280] italic mt-1.5 pl-3 border-l-2 border-[#D97706]">"{e}"</p>
          ))}
          {expanded && trend.implications?.length > 0 && (
            <div className="mt-2">
              <p className="text-[10px] font-bold text-[#4B5563] uppercase mb-1">Implications</p>
              {trend.implications.map((imp, i) => (
                <p key={`wm-5-${i}`} className="text-xs text-[#4B5563] flex items-start gap-1.5 mt-0.5"><span className="text-[#D97706]">-</span>{imp}</p>
              ))}
            </div>
          )}
        </div>
        {/* Strength gauge */}
        <div className="flex-shrink-0 w-12 h-12 rounded-full border-3 flex items-center justify-center"
          style={{ borderColor: trend.strength >= 70 ? '#059669' : trend.strength >= 40 ? '#D97706' : '#DC2626', borderWidth: '3px' }}>
          <span className="text-xs font-bold text-[#111827]">{trend.strength}</span>
        </div>
      </div>
    </div>
  );
}

function ToolResultMedia({ result, apiUrl }) {
  return (
    <>
      {result.audio_base64 && (
        <div className="mb-6 p-4 bg-[#BE185D]/5 border border-[#BE185D]/20 rounded-lg">
          <p className="text-sm font-semibold text-[#BE185D] mb-2">Audio Briefing</p>
          <audio controls className="w-full" src={`data:audio/mp3;base64,${result.audio_base64}`} data-testid="audio-player" />
          {result.audio_file && <a href={`${apiUrl}/api/tools/download/${result.audio_file}`} className="inline-flex items-center gap-1.5 mt-2 text-xs text-[#BE185D] hover:underline">Download MP3</a>}
        </div>
      )}
      {result.download_file && (
        <div className="mb-6 p-4 bg-[#0891B2]/5 border border-[#0891B2]/20 rounded-lg">
          <p className="text-sm font-semibold text-[#0891B2] mb-2">Presentation Ready ({result.slide_count || 0} slides)</p>
          <a href={`${apiUrl}/api/tools/download/${result.download_file}`} className="inline-flex items-center gap-2 px-4 py-2 bg-[#0891B2] text-white rounded-xl text-sm font-medium hover:bg-[#0891B2]/90 transition-colors" data-testid="download-pptx-fullscreen">Download PPTX</a>
        </div>
      )}
      {result.slides && <SlidePreview slides={result.slides} />}
    </>
  );
}

function SlidePreview({ slides }) {
  return (
    <div className="mb-6">
      <h3 className="text-sm font-bold text-[#111827] mb-3">Slide Preview</h3>
      <div className="grid grid-cols-2 gap-3">
        {slides.map((slide, i) => (
          <div key={`wm-6-${i}`} className="p-4 bg-white border border-[#E5E7EB] rounded-lg shadow-sm">
            <p className="text-xs font-bold text-[#6590FF] mb-2">Slide {i + 1}: {slide.title}</p>
            <ul className="space-y-1">{slide.bullets?.map((b, j) => <li key={j} className="text-xs text-[#4B5563]">{b}</li>)}</ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function ToolResultContent({ result }) {
  const listKeys = [
    'key_findings', 'decisions_needed', 'risks', 'recommendations', 'conclusions',
    'alerts', 'gaps', 'conflicts', 'changes', 'decisions', 'action_items',
    'key_provisions', 'obligations', 'scenarios', 'affected_entities', 'nodes',
    'inconsistencies', 'qa_pairs', 'checklist', 'proposals', 'alignment_issues'
  ];

  return (
    <>
      {result.script && (
        <div className="mb-6 p-5 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg">
          <h3 className="text-sm font-bold text-[#111827] mb-2">Briefing Script</h3>
          <p className="text-sm text-[#4B5563] leading-relaxed whitespace-pre-wrap">{result.script}</p>
        </div>
      )}
      {result.tables && <TablesView tables={result.tables} />}
      {result.flashcards && <FlashcardsView flashcards={result.flashcards} />}
      {result.quiz && <QuizView quiz={result.quiz} />}
      {result.kpi_summary && <KPIView kpis={result.kpi_summary} />}
      {result.summary && typeof result.summary === 'string' && (
        <div className="mb-6 p-5 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg">
          <p className="text-sm text-[#4B5563] leading-relaxed">{result.summary}</p>
        </div>
      )}
      {result.executive_summary && (
        <div className="mb-6 p-5 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg">
          <h3 className="text-sm font-bold text-[#111827] mb-2">Executive Summary</h3>
          <p className="text-sm text-[#4B5563] leading-relaxed">{result.executive_summary}</p>
        </div>
      )}
      {listKeys.filter(key => result[key] && Array.isArray(result[key]) && result[key].length > 0).map(key => (
        <GenericList key={key} title={key} items={result[key]} />
      ))}
      {result.sections && result.sections[0]?.content && <SectionsView sections={result.sections} />}
      {result.study_guide && (
        <div className="mb-6 p-5 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg">
          <h3 className="text-sm font-bold text-[#111827] mb-2">Study Guide</h3>
          <div className="prose prose-sm max-w-none text-[#4B5563]"><ReactMarkdown remarkPlugins={[remarkGfm]}>{result.study_guide}</ReactMarkdown></div>
        </div>
      )}
      {result.hero_stat && (
        <div className="mb-6 text-center p-8 bg-[#6590FF]/5 rounded-xl">
          <p className="text-4xl font-bold text-[#6590FF]">{result.hero_stat.value}</p>
          <p className="text-sm text-[#4B5563] mt-1">{result.hero_stat.label}</p>
        </div>
      )}
      {result.raw_text && (
        <div className="p-5 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg">
          <div className="prose prose-sm max-w-none text-[#4B5563]"><ReactMarkdown remarkPlugins={[remarkGfm]}>{result.raw_text}</ReactMarkdown></div>
        </div>
      )}
      {result.error && (
        <div className="p-4 bg-[#FEE2E2] border border-[#DC2626]/20 rounded-lg">
          <p className="text-sm text-[#DC2626] font-medium">{result.error}</p>
        </div>
      )}
    </>
  );
}

function TablesView({ tables }) {
  return (
    <div className="mb-6 space-y-4">
      {tables.map((table, i) => (
        <div key={`wm-7-${i}`} className="border border-[#E5E7EB] rounded-lg overflow-hidden">
          <div className="bg-[#F9FAFB] px-4 py-2 border-b border-[#E5E7EB]">
            <p className="text-sm font-bold text-[#111827]">{table.name}</p>
            {table.description && <p className="text-xs text-[#6B7280]">{table.description}</p>}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="bg-[#F3F4F6]">{table.headers?.map((h, j) => <th key={`h-${j}-${h}`} className="px-3 py-2 text-left font-semibold text-[#111827]">{h}</th>)}</tr></thead>
              <tbody>{table.rows?.map((row, j) => <tr key={`r-${j}`} className="border-t border-[#E5E7EB]">{row.map((cell, k) => <td key={`c-${k}`} className="px-3 py-2 text-[#4B5563]">{cell}</td>)}</tr>)}</tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

function FlashcardsView({ flashcards }) {
  return (
    <div className="mb-6">
      <h3 className="text-sm font-bold text-[#111827] mb-3">Flashcards ({flashcards.length})</h3>
      <div className="grid grid-cols-2 gap-3">
        {flashcards.map((card, i) => (
          <div key={`wm-8-${i}`} className="p-4 border border-[#E5E7EB] rounded-lg bg-[#EEF2FF]/50">
            <p className="text-xs font-bold text-[#6590FF] mb-2">Q: {card.front}</p>
            <p className="text-xs text-[#4B5563]">A: {card.back}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function QuizView({ quiz }) {
  return (
    <div className="mb-6">
      <h3 className="text-sm font-bold text-[#111827] mb-3">Quiz ({quiz.length} questions)</h3>
      <div className="space-y-4">
        {quiz.map((q, i) => (
          <div key={`wm-9-${i}`} className="p-4 border border-[#E5E7EB] rounded-lg">
            <p className="text-sm font-semibold text-[#111827] mb-2">{i + 1}. {q.question}</p>
            <div className="space-y-1 ml-4">{q.options?.map((opt, j) => <p key={j} className={`text-xs ${opt.startsWith(q.correct) ? 'text-[#059669] font-bold' : 'text-[#4B5563]'}`}>{opt}</p>)}</div>
            {q.explanation && <p className="text-xs text-[#6B7280] mt-2 italic">{q.explanation}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

function KPIView({ kpis }) {
  return (
    <div className="mb-6 grid grid-cols-3 gap-3">
      {kpis.map((kpi, i) => (
        <div key={`wm-10-${i}`} className={`p-4 rounded-lg border ${kpi.status === 'good' ? 'bg-[#ECFDF5] border-[#059669]/20' : kpi.status === 'warning' ? 'bg-[#FEF3C7] border-[#D97706]/20' : kpi.status === 'critical' ? 'bg-[#FEE2E2] border-[#DC2626]/20' : 'bg-[#F3F4F6] border-[#E5E7EB]'}`}>
          <p className="text-lg font-bold text-[#111827]">{kpi.value}</p>
          <p className="text-xs text-[#4B5563]">{kpi.metric}</p>
        </div>
      ))}
    </div>
  );
}

function SectionsView({ sections }) {
  return (
    <div className="mb-6 space-y-4">
      {sections.map((section, i) => (
        <div key={`wm-11-${i}`} className="border border-[#E5E7EB] rounded-lg overflow-hidden">
          <div className="bg-[#F9FAFB] px-4 py-2 border-b border-[#E5E7EB]">
            <p className="text-sm font-bold text-[#111827]">{section.heading || section.title}</p>
          </div>
          <div className="p-4 prose prose-sm max-w-none text-[#4B5563]">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{section.content}</ReactMarkdown>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---- Access Guard Dashboard ----
export function FullscreenAccessGuard({ data }) {
  return (
    <div className="max-w-5xl mx-auto p-8" data-testid="fullscreen-access-guard">
      <h2 className="text-xl font-bold text-[#111827] mb-6" style={{ fontFamily: 'var(--font-heading)' }}>Access Control Dashboard</h2>
      {data.workspace && (
        <div className="mb-6 p-4 bg-[#EEF2FF] border border-[#6590FF]/20 rounded-lg">
          <p className="text-sm font-semibold text-[#6590FF]">Workspace: {data.workspace.name}</p>
          {data.owner && <p className="text-xs text-[#4B5563] mt-1">Owner: {data.owner.name} ({data.owner.email}) - {data.owner.role}</p>}
        </div>
      )}
      <UsersTable users={data.users} />
      {data.permissions && <PermissionsGrid permissions={data.permissions} />}
    </div>
  );
}

function UsersTable({ users }) {
  return (
    <>
      <h3 className="text-sm font-bold text-[#111827] mb-3">Users ({users?.length || 0})</h3>
      <div className="border border-[#E5E7EB] rounded-lg overflow-hidden mb-6">
        <table className="w-full text-xs">
          <thead><tr className="bg-[#F3F4F6]">
            <th className="px-4 py-2 text-left font-semibold">Name</th>
            <th className="px-4 py-2 text-left font-semibold">Email</th>
            <th className="px-4 py-2 text-left font-semibold">Role</th>
            <th className="px-4 py-2 text-left font-semibold">Status</th>
            <th className="px-4 py-2 text-left font-semibold">Joined</th>
          </tr></thead>
          <tbody>
            {users?.map((u, i) => (
              <tr key={`wm-12-${i}`} className="border-t border-[#E5E7EB]">
                <td className="px-4 py-2 text-[#111827] font-medium">{u.name}</td>
                <td className="px-4 py-2 text-[#4B5563]">{u.email}</td>
                <td className="px-4 py-2"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${u.role === 'Admin' ? 'bg-[#FEE2E2] text-[#DC2626]' : 'bg-[#EEF2FF] text-[#6590FF]'}`}>{u.role}</span></td>
                <td className="px-4 py-2"><span className={`px-2 py-0.5 rounded-full text-[10px] ${u.active ? 'bg-[#ECFDF5] text-[#059669]' : 'bg-[#F3F4F6] text-[#6B7280]'}`}>{u.active ? 'Active' : 'Inactive'}</span></td>
                <td className="px-4 py-2 text-[#6B7280]">{new Date(u.joined).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function PermissionsGrid({ permissions }) {
  return (
    <div>
      <h3 className="text-sm font-bold text-[#111827] mb-3">Role Permissions</h3>
      <div className="grid grid-cols-2 gap-4">
        {Object.entries(permissions).map(([role, perms]) => (
          <div key={role} className="p-4 border border-[#E5E7EB] rounded-lg">
            <p className="text-sm font-bold text-[#111827] mb-2">{role}</p>
            <div className="space-y-1">
              {perms.map((p, i) => (
                <p key={`wm-13-${i}`} className="text-xs text-[#4B5563] flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#059669]" />
                  {p.replace(/_/g, ' ')}
                </p>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- Audit Trail Dashboard ----
export function FullscreenAuditTrail({ data }) {
  return (
    <div className="max-w-5xl mx-auto p-8" data-testid="fullscreen-audit-trail">
      <h2 className="text-xl font-bold text-[#111827] mb-2" style={{ fontFamily: 'var(--font-heading)' }}>Audit Trail</h2>
      <p className="text-sm text-[#6B7280] mb-6">{data.total_entries || 0} entries recorded</p>
      <AuditTable entries={data.entries} />
      {(!data.entries || data.entries.length === 0) && <div className="py-12 text-center"><p className="text-sm text-[#6B7280]">No audit entries found</p></div>}
    </div>
  );
}

function AuditTable({ entries }) {
  return (
    <div className="border border-[#E5E7EB] rounded-lg overflow-hidden">
      <table className="w-full text-xs">
        <thead><tr className="bg-[#F3F4F6]">
          <th className="px-4 py-2 text-left font-semibold">Date</th>
          <th className="px-4 py-2 text-left font-semibold">User</th>
          <th className="px-4 py-2 text-left font-semibold">Action</th>
          <th className="px-4 py-2 text-left font-semibold">Resource</th>
        </tr></thead>
        <tbody>
          {entries?.map((entry, i) => (
            <tr key={`wm-14-${i}`} className="border-t border-[#E5E7EB] hover:bg-[#F9FAFB]">
              <td className="px-4 py-2 text-[#6B7280]">{new Date(entry.date).toLocaleString()}</td>
              <td className="px-4 py-2 text-[#111827] font-medium">{entry.user}</td>
              <td className="px-4 py-2">
                <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${entry.action.includes('delete') ? 'bg-[#FEE2E2] text-[#DC2626]' : entry.action.includes('create') || entry.action.includes('upload') ? 'bg-[#ECFDF5] text-[#059669]' : entry.action.includes('run') ? 'bg-[#EEF2FF] text-[#6590FF]' : 'bg-[#F3F4F6] text-[#4B5563]'}`}>{entry.action.replace(/_/g, ' ')}</span>
              </td>
              <td className="px-4 py-2 text-[#4B5563]">{entry.resource_type} {entry.resource_id ? `(${entry.resource_id.slice(0, 8)}...)` : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---- LLM Settings Modal (Two-Tab Design) ----
const PRESET_LLMS = [
  { name: 'Claude Sonnet 4.5', provider: 'anthropic', model_name: 'claude-sonnet-4-5-20250929', category: 'Anthropic' },
  { name: 'Claude Opus 4.5', provider: 'anthropic', model_name: 'claude-opus-4-5-20250929', category: 'Anthropic' },
  { name: 'Claude Haiku 4.5', provider: 'anthropic', model_name: 'claude-haiku-4-5-20250929', category: 'Anthropic' },
  { name: 'GPT-5.2', provider: 'openai', model_name: 'gpt-5.2', category: 'OpenAI' },
  { name: 'GPT-4o', provider: 'openai', model_name: 'gpt-4o', category: 'OpenAI' },
  { name: 'GPT-4o Mini', provider: 'openai', model_name: 'gpt-4o-mini', category: 'OpenAI' },
  { name: 'Gemini 3 Pro', provider: 'google', model_name: 'gemini-3-pro', category: 'Google' },
  { name: 'Gemini 3 Flash', provider: 'google', model_name: 'gemini-3-flash', category: 'Google' },
  { name: 'Claude 3.5 Sonnet', provider: 'openrouter', model_name: 'openrouter/anthropic/claude-3.5-sonnet', category: 'OpenRouter' },
  { name: 'Llama 3.3 70B', provider: 'openrouter', model_name: 'openrouter/meta-llama/llama-3.3-70b-instruct', category: 'OpenRouter' },
  { name: 'Mistral Large', provider: 'openrouter', model_name: 'openrouter/mistralai/mistral-large-latest', category: 'OpenRouter' },
  { name: 'DeepSeek V3', provider: 'openrouter', model_name: 'openrouter/deepseek/deepseek-chat-v3-0324', category: 'OpenRouter' },
  { name: 'Qwen 2.5 72B', provider: 'openrouter', model_name: 'openrouter/qwen/qwen-2.5-72b-instruct', category: 'OpenRouter' },
  { name: 'OCI GenAI (Cohere)', provider: 'oci', model_name: 'cohere.command-r-plus', category: 'Oracle Cloud' },
  { name: 'OCI GenAI (Llama)', provider: 'oci', model_name: 'meta.llama-3.1-405b', category: 'Oracle Cloud' },
  { name: 'Azure OpenAI', provider: 'azure', model_name: 'gpt-4o', category: 'Azure' },
  { name: 'AWS Bedrock', provider: 'bedrock', model_name: 'anthropic.claude-3-sonnet', category: 'AWS' },
];

const PROVIDER_OPTIONS = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'google', label: 'Google' },
  { value: 'openrouter', label: 'OpenRouter' },
  { value: 'azure', label: 'Azure OpenAI' },
  { value: 'oci', label: 'OCI GenAI' },
  { value: 'bedrock', label: 'AWS Bedrock' },
  { value: 'custom', label: 'Custom / Self-Hosted' },
];

export function LLMSettingsModal({ workspaceId, onClose, initialConfigs, onConfigsChange }) {
  const [activeTab, setActiveTab] = useState('current');
  const [configs, setConfigs] = useState(initialConfigs || []);
  const [loading, setLoading] = useState(initialConfigs === null);
  const [search, setSearch] = useState('');
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [newConfig, setNewConfig] = useState({ name: '', provider: 'custom', base_url: '', api_key: '', model_name: '', is_active: true });
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  // Error Log & Usage
  const [errorLogs, setErrorLogs] = useState(null);
  const [usageData, setUsageData] = useState(null);

  useEffect(() => {
    if (initialConfigs !== null) { setConfigs(initialConfigs); setLoading(false); }
  }, [initialConfigs]);

  const updateConfigs = (newConfigs) => { setConfigs(newConfigs); if (onConfigsChange) onConfigsChange(newConfigs); };

  // Lazy-load Error Logs and Usage
  useEffect(() => {
    if (activeTab === 'errors' && errorLogs === null) {
      systemAPI.errorLog().then(r => setErrorLogs(r.data || [])).catch(() => setErrorLogs([]));
    }
    if (activeTab === 'usage' && usageData === null) {
      systemAPI.usage().then(r => setUsageData(r.data || {})).catch(() => setUsageData({}));
    }
  }, [activeTab]);

  const toggleActive = async (id, currentActive) => {
    try { const res = await llmConfigAPI.update(id, { is_active: !currentActive }); updateConfigs(configs.map(c => c.id === id ? { ...c, is_active: res.data.is_active } : c)); } catch (e) { console.error(e); }
  };
  const startEdit = (config) => { setEditId(config.id); setEditData({ name: config.name, provider: config.provider, base_url: config.base_url || '', api_key: '', model_name: config.model_name }); };
  const saveEdit = async () => {
    setSaving(true);
    try { const payload = { ...editData }; if (!payload.api_key) delete payload.api_key; const res = await llmConfigAPI.update(editId, payload); updateConfigs(configs.map(c => c.id === editId ? { ...c, ...res.data } : c)); setEditId(null); setEditData({}); } catch (e) { console.error(e); } finally { setSaving(false); }
  };
  const removeConfig = async (id) => { try { await llmConfigAPI.remove(id); updateConfigs(configs.filter(c => c.id !== id)); } catch (e) { console.error(e); } };
  const addPreset = async (preset) => {
    setSaving(true);
    try { const res = await llmConfigAPI.create({ name: preset.name, provider: preset.provider, model_name: preset.model_name, is_active: true }); updateConfigs([res.data, ...configs]); setQuickAddOpen(false); } catch (e) { console.error(e); } finally { setSaving(false); }
  };
  const addCustom = async () => {
    if (!newConfig.name.trim() || !newConfig.model_name.trim()) return;
    setSaving(true);
    try { const res = await llmConfigAPI.create(newConfig); updateConfigs([res.data, ...configs]); setNewConfig({ name: '', provider: 'custom', base_url: '', api_key: '', model_name: '', is_active: true }); setActiveTab('current'); } catch (e) { console.error(e); } finally { setSaving(false); }
  };
  const filtered = configs.filter(c => { if (!search.trim()) return true; const q = search.toLowerCase(); return c.name?.toLowerCase().includes(q) || c.provider?.toLowerCase().includes(q) || c.model_name?.toLowerCase().includes(q); });
  const categories = [...new Set(PRESET_LLMS.map(p => p.category))];

  const tabs = [
    { key: 'current', label: 'Models', badge: configs.length || null },
    { key: 'byom', label: 'Add Model' },
    { key: 'routing', label: 'Routing' },
    { key: 'usage', label: 'Usage' },
    { key: 'errors', label: 'Error Log' },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="llm-settings-overlay">
      <div className="bg-white w-full max-w-2xl max-h-[85vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="border-b border-[#E5E7EB] px-6 pt-5 pb-0">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-[#6590FF]/10 flex items-center justify-center">
                <Settings className="w-4 h-4 text-[#6590FF]" />
              </div>
              <span className="text-base font-bold text-[#111827]" style={{ fontFamily: 'var(--font-heading)' }}>Settings</span>
            </div>
            <button onClick={onClose} className="p-1.5 hover:bg-[#F3F4F6] rounded-xl transition-colors" data-testid="close-llm-settings">
              <X className="w-5 h-5 text-[#4B5563]" />
            </button>
          </div>
          <div className="flex gap-0 overflow-x-auto" data-testid="llm-settings-tabs">
            {tabs.map(t => (
              <button key={t.key} onClick={() => setActiveTab(t.key)}
                className={`px-3 pb-3 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${activeTab === t.key ? 'text-[#6590FF] border-[#6590FF]' : 'text-[#6B7280] border-transparent hover:text-[#111827]'}`}
                data-testid={`tab-${t.key}`}>
                {t.label}
                {t.badge > 0 && <span className="ml-1.5 text-[10px] bg-[#6590FF]/10 text-[#6590FF] px-1.5 py-0.5 rounded-full font-bold">{t.badge}</span>}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {activeTab === 'current' && <CurrentModelsTab configs={filtered} loading={loading} search={search} setSearch={setSearch} editId={editId} editData={editData} setEditData={setEditData} startEdit={startEdit} saveEdit={saveEdit} cancelEdit={() => { setEditId(null); setEditData({}); }} saving={saving} toggleActive={toggleActive} removeConfig={removeConfig} quickAddOpen={quickAddOpen} setQuickAddOpen={setQuickAddOpen} categories={categories} addPreset={addPreset} />}
          {activeTab === 'byom' && <BYOMTab newConfig={newConfig} setNewConfig={setNewConfig} saving={saving} onAdd={addCustom} />}
          {activeTab === 'routing' && <RoutingTab workspaceId={workspaceId} />}
          {activeTab === 'usage' && <UsageTab data={usageData} />}
          {activeTab === 'errors' && <ErrorLogTab logs={errorLogs} />}
        </div>
      </div>
    </div>
  );
}

// ---- Tab 1: Current Models ----
function CurrentModelsTab({ configs, loading, search, setSearch, editId, editData, setEditData, startEdit, saveEdit, cancelEdit, saving, toggleActive, removeConfig, quickAddOpen, setQuickAddOpen, categories, addPreset }) {
  return (
    <div className="p-5">
      {/* Search + Quick Add row */}
      <div className="flex gap-2 mb-4">
        <div className="flex-1 relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search models by name, provider, or model ID..."
            className="w-full h-10 pl-9 pr-3 text-sm border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#6590FF] focus:ring-1 focus:ring-[#6590FF]/20 bg-white"
            data-testid="search-models-input"
          />
        </div>
        <button
          onClick={() => setQuickAddOpen(!quickAddOpen)}
          className="h-10 px-3.5 bg-[#6590FF] hover:bg-[#4B7CFF] text-white text-sm font-medium rounded-xl transition-colors flex items-center gap-1.5 whitespace-nowrap"
          data-testid="quick-add-model-btn"
        >
          <Plus className="w-4 h-4" /> Quick Add
        </button>
      </div>

      {/* Quick Add Preset Dropdown */}
      {quickAddOpen && (
        <div className="mb-4 p-4 bg-[#F8FAFC] border border-[#E5E7EB] rounded-xl" data-testid="quick-add-panel">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold text-[#111827]" style={{ fontFamily: 'var(--font-heading)' }}>Add from Presets</p>
            <button onClick={() => setQuickAddOpen(false)} className="text-xs text-[#6B7280] hover:text-[#111827]">Close</button>
          </div>
          <div className="grid grid-cols-2 gap-1.5 max-h-[200px] overflow-y-auto">
            {categories.map(cat => (
              PRESET_LLMS.filter(p => p.category === cat).map(preset => (
                <button
                  key={preset.model_name}
                  onClick={() => addPreset(preset)}
                  className="flex items-center gap-2 p-2 border border-[#E5E7EB] rounded-lg hover:border-[#6590FF] hover:bg-white transition-all text-left"
                  data-testid={`quick-add-${preset.model_name}`}
                >
                  <Plus className="w-3 h-3 text-[#6590FF] flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-[#111827] truncate">{preset.name}</p>
                    <p className="text-[9px] text-[#9CA3AF] truncate">{cat}</p>
                  </div>
                </button>
              ))
            ))}
          </div>
        </div>
      )}

      {/* Model List */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-[#6590FF] animate-spin" /></div>
      ) : configs.length === 0 ? (
        <div className="text-center py-12 px-6">
          <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-[#F3F4F6] flex items-center justify-center">
            <Brain className="w-6 h-6 text-[#9CA3AF]" />
          </div>
          <p className="text-sm font-semibold text-[#111827] mb-1">No models configured</p>
          <p className="text-xs text-[#6B7280] mb-4">{search ? 'No models match your search.' : 'Use "Quick Add" to add a preset, or go to "Bring Your Own Model" to add a custom one.'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {configs.map(c => (
            editId === c.id ? (
              <ModelEditRow key={c.id} config={c} editData={editData} setEditData={setEditData} onSave={saveEdit} onCancel={cancelEdit} saving={saving} />
            ) : (
              <ModelRow key={c.id} config={c} onToggle={toggleActive} onEdit={startEdit} onRemove={removeConfig} />
            )
          ))}
        </div>
      )}
    </div>
  );
}

// Single model row (view mode)
function ModelRow({ config, onToggle, onEdit, onRemove }) {
  const c = config;
  return (
    <div className="flex items-center gap-3 p-3.5 border border-[#E5E7EB] rounded-xl group hover:border-[#6590FF]/20 transition-all" data-testid={`model-row-${c.id}`}>
      {/* Active Checkbox */}
      <button
        onClick={() => onToggle(c.id, c.is_active)}
        className="flex-shrink-0"
        data-testid={`toggle-model-${c.id}`}
        title={c.is_active ? 'Deactivate model' : 'Activate model'}
      >
        <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${c.is_active ? 'bg-[#6590FF] border-[#6590FF]' : 'border-[#D1D5DB] hover:border-[#6590FF]/50'}`}>
          {c.is_active && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
        </div>
      </button>

      {/* Model icon */}
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${c.is_active ? 'bg-[#6590FF]/8' : 'bg-[#F3F4F6]'}`}>
        <Brain className={`w-4 h-4 ${c.is_active ? 'text-[#6590FF]' : 'text-[#9CA3AF]'}`} />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className={`text-sm font-semibold truncate ${c.is_active ? 'text-[#111827]' : 'text-[#6B7280]'}`}>{c.name}</p>
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${c.is_active ? 'bg-[#ECFDF5] text-[#059669]' : 'bg-[#F3F4F6] text-[#9CA3AF]'}`}>
            {c.is_active ? 'Active' : 'Inactive'}
          </span>
        </div>
        <p className="text-xs text-[#9CA3AF] truncate mt-0.5">
          <span className="text-[#6B7280] font-medium">{c.provider}</span> / {c.model_name}
          {c.base_url ? <span className="ml-1 text-[#D1D5DB]">{c.base_url}</span> : ''}
          {c.api_key ? <span className="ml-1.5 text-[10px] bg-[#FEF3C7] text-[#92400E] px-1 py-0.5 rounded">Key: {c.api_key}</span> : ''}
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => onEdit(c)}
          className="p-1.5 hover:bg-[#EEF2FF] rounded-lg transition-colors"
          data-testid={`edit-model-${c.id}`}
          title="Edit model"
        >
          <svg className="w-3.5 h-3.5 text-[#6590FF]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
        </button>
        <button
          onClick={() => onRemove(c.id)}
          className="p-1.5 hover:bg-[#FEE2E2] rounded-lg transition-colors"
          data-testid={`remove-model-${c.id}`}
          title="Remove model"
        >
          <Trash2 className="w-3.5 h-3.5 text-[#DC2626]" />
        </button>
      </div>
    </div>
  );
}

// Inline edit row
function ModelEditRow({ config, editData, setEditData, onSave, onCancel, saving }) {
  return (
    <div className="p-4 border-2 border-[#6590FF]/30 rounded-xl bg-[#F8FAFC] space-y-3" data-testid={`edit-row-${config.id}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold text-[#6590FF]" style={{ fontFamily: 'var(--font-heading)' }}>Editing: {config.name}</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] font-medium text-[#4B5563] mb-1 block">Display Name</label>
          <input
            value={editData.name || ''}
            onChange={e => setEditData({ ...editData, name: e.target.value })}
            className="w-full h-9 px-3 text-sm border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#6590FF] bg-white"
            data-testid="edit-model-name"
          />
        </div>
        <div>
          <label className="text-[11px] font-medium text-[#4B5563] mb-1 block">Provider</label>
          <select
            value={editData.provider || ''}
            onChange={e => setEditData({ ...editData, provider: e.target.value })}
            className="w-full h-9 px-3 text-sm border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#6590FF] bg-white"
            data-testid="edit-model-provider"
          >
            {PROVIDER_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="text-[11px] font-medium text-[#4B5563] mb-1 block">Model ID</label>
        <input
          value={editData.model_name || ''}
          onChange={e => setEditData({ ...editData, model_name: e.target.value })}
          className="w-full h-9 px-3 text-sm border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#6590FF] bg-white"
          data-testid="edit-model-id"
        />
      </div>
      <div>
        <label className="text-[11px] font-medium text-[#4B5563] mb-1 block">Base URL / Endpoint</label>
        <input
          value={editData.base_url || ''}
          onChange={e => setEditData({ ...editData, base_url: e.target.value })}
          placeholder="https://your-endpoint.com/v1"
          className="w-full h-9 px-3 text-sm border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#6590FF] bg-white"
          data-testid="edit-model-url"
        />
      </div>
      <div>
        <label className="text-[11px] font-medium text-[#4B5563] mb-1 block">API Key <span className="text-[#9CA3AF] font-normal">(leave blank to keep current)</span></label>
        <input
          type="password"
          autoComplete="off"
          name="byo-edit-api-key"
          data-1p-ignore="true"
          data-lpignore="true"
          value={editData.api_key || ''}
          onChange={e => setEditData({ ...editData, api_key: e.target.value })}
          placeholder="Enter new key to update, or leave blank"
          className="w-full h-9 px-3 text-sm border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#6590FF] bg-white"
          data-testid="edit-model-key"
        />
      </div>
      <div className="flex gap-2 pt-1">
        <button
          onClick={onSave}
          disabled={saving}
          className="flex-1 h-9 bg-[#6590FF] hover:bg-[#4B7CFF] text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50"
          data-testid="save-edit-model"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
        <button
          onClick={onCancel}
          className="h-9 px-4 border border-[#E5E7EB] text-sm font-medium text-[#4B5563] rounded-xl hover:bg-[#F3F4F6] transition-colors"
          data-testid="cancel-edit-model"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ---- Tab: Error Log ----
function ErrorLogTab({ logs }) {
  const [search, setSearch] = useState('');
  if (logs === null) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-[#6590FF] animate-spin" /></div>;

  const filtered = logs.filter(l => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return l.error_type?.toLowerCase().includes(q) || l.message?.toLowerCase().includes(q) || l.endpoint?.toLowerCase().includes(q);
  });

  return (
    <div className="p-5" data-testid="error-log-tab">
      <div className="flex items-center gap-2 mb-4">
        <div className="flex-1 relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search errors..."
            className="w-full h-9 pl-9 pr-3 text-sm border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#6590FF] bg-white" data-testid="error-log-search" />
        </div>
        <span className="text-xs text-[#9CA3AF] whitespace-nowrap">{filtered.length} error{filtered.length !== 1 ? 's' : ''}</span>
      </div>
      {filtered.length === 0 ? (
        <div className="text-center py-12">
          <AlertTriangle className="w-8 h-8 text-[#D1D5DB] mx-auto mb-2" />
          <p className="text-sm text-[#6B7280]">{search ? 'No matching errors.' : 'No errors recorded yet.'}</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {filtered.map(l => (
            <ErrorLogRow key={l.id} log={l} />
          ))}
        </div>
      )}
    </div>
  );
}

function ErrorLogRow({ log }) {
  const [expanded, setExpanded] = useState(false);
  const severity = log.error_type?.includes('critical') ? 'bg-[#DC2626]' : log.error_type?.includes('warning') ? 'bg-[#D97706]' : 'bg-[#6B7280]';
  return (
    <div className="p-3 border border-[#E5E7EB] rounded-xl hover:border-[#DC2626]/20 transition-colors" data-testid={`error-row-${log.id}`}>
      <div className="flex items-start gap-2.5 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${severity}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-bold text-[#111827]">{log.error_type || 'Error'}</span>
            {log.endpoint && <span className="text-[10px] text-[#9CA3AF] bg-[#F3F4F6] px-1.5 py-0.5 rounded font-mono">{log.endpoint}</span>}
          </div>
          <p className="text-xs text-[#4B5563] mt-0.5 line-clamp-2">{log.message}</p>
          <p className="text-[10px] text-[#9CA3AF] mt-1">{log.date ? new Date(log.date).toLocaleString() : ''}</p>
        </div>
        {expanded ? <ChevronDown className="w-3.5 h-3.5 text-[#9CA3AF] flex-shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-[#9CA3AF] flex-shrink-0" />}
      </div>
      {expanded && (
        <div className="mt-2 pt-2 border-t border-[#F3F4F6]">
          <p className="text-xs text-[#4B5563] whitespace-pre-wrap font-mono bg-[#F9FAFB] p-2 rounded-lg max-h-32 overflow-y-auto">{log.message}</p>
        </div>
      )}
    </div>
  );
}

// ---- Tab: Usage Analytics ----
function UsageTab({ data }) {
  if (data === null) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-[#6590FF] animate-spin" /></div>;

  const chatUsage = data.chat_usage || [];
  const toolUsage = data.tool_usage || [];
  const sourceCounts = data.source_counts || [];
  const recentActivity = data.recent_activity || [];

  const totalMessages = chatUsage.reduce((s, c) => s + (c.messages || 0), 0);
  const totalSessions = chatUsage.reduce((s, c) => s + (c.sessions || 0), 0);
  const totalRuns = toolUsage.reduce((s, t) => s + (t.runs || 0), 0);
  const totalSources = sourceCounts.reduce((s, c) => s + (c.docs || 0) + (c.web || 0) + (c.text || 0), 0);

  return (
    <div className="p-5" data-testid="usage-tab">
      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-3 mb-5">
        {[
          { label: 'Messages', value: totalMessages, color: '#6590FF' },
          { label: 'Sessions', value: totalSessions, color: '#059669' },
          { label: 'Tool Runs', value: totalRuns, color: '#D97706' },
          { label: 'Sources', value: totalSources, color: '#7C3AED' },
        ].map(s => (
          <div key={s.label} className="p-3 bg-white border border-[#E5E7EB] rounded-xl text-center">
            <p className="text-xl font-bold" style={{ color: s.color }}>{s.value}</p>
            <p className="text-[10px] text-[#6B7280] mt-0.5 font-medium">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Chat usage per workspace */}
      {chatUsage.length > 0 && (
        <div className="mb-5">
          <h4 className="text-xs font-bold text-[#111827] uppercase tracking-wider mb-2">Chat Usage by Workspace</h4>
          <div className="space-y-1.5">
            {chatUsage.map((c, i) => (
              <div key={`wm-15-${i}`} className="flex items-center gap-3 p-2.5 bg-[#F9FAFB] rounded-xl">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#111827] truncate">{c.workspace}</p>
                </div>
                <div className="flex items-center gap-3 text-xs text-[#6B7280] flex-shrink-0">
                  <span><b className="text-[#6590FF]">{c.messages}</b> msgs</span>
                  <span><b className="text-[#059669]">{c.sessions}</b> sessions</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tool usage */}
      {toolUsage.length > 0 && (
        <div className="mb-5">
          <h4 className="text-xs font-bold text-[#111827] uppercase tracking-wider mb-2">Tool Usage</h4>
          <div className="space-y-1.5">
            {toolUsage.slice(0, 15).map((t, i) => (
              <div key={`wm-16-${i}`} className="flex items-center gap-3 p-2.5 bg-[#F9FAFB] rounded-xl">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#111827] truncate">{t.tool?.replace(/_/g, ' ').replace('tool:', '')}</p>
                  <p className="text-[10px] text-[#9CA3AF]">{t.workspace}</p>
                </div>
                <div className="flex items-center gap-2 text-xs flex-shrink-0">
                  <span className="text-[#059669] font-medium">{t.success} ok</span>
                  {t.fail > 0 && <span className="text-[#DC2626] font-medium">{t.fail} fail</span>}
                  <span className="text-[#6B7280]">{t.runs} total</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Source counts */}
      {sourceCounts.length > 0 && (
        <div className="mb-5">
          <h4 className="text-xs font-bold text-[#111827] uppercase tracking-wider mb-2">Sources by Workspace</h4>
          <div className="space-y-1.5">
            {sourceCounts.map((s, i) => (
              <div key={`wm-17-${i}`} className="flex items-center gap-3 p-2.5 bg-[#F9FAFB] rounded-xl">
                <p className="flex-1 text-sm font-medium text-[#111827] truncate">{s.workspace}</p>
                <div className="flex items-center gap-2 text-xs text-[#6B7280] flex-shrink-0">
                  <span>{s.docs} docs</span>
                  <span>{s.web} web</span>
                  <span>{s.text} text</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent activity */}
      {recentActivity.length > 0 && (
        <div>
          <h4 className="text-xs font-bold text-[#111827] uppercase tracking-wider mb-2">Recent Activity</h4>
          <div className="space-y-1 max-h-[200px] overflow-y-auto">
            {recentActivity.slice(0, 20).map((a, i) => (
              <div key={`wm-18-${i}`} className="flex items-center gap-2 py-1.5 text-xs">
                <span className="w-1.5 h-1.5 rounded-full bg-[#6590FF] flex-shrink-0" />
                <span className="text-[#111827] font-medium">{a.action?.replace(/_/g, ' ')}</span>
                {a.workspace && <span className="text-[#9CA3AF]">in {a.workspace}</span>}
                <span className="ml-auto text-[#9CA3AF] flex-shrink-0">{a.date ? new Date(a.date).toLocaleDateString() : ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {chatUsage.length === 0 && toolUsage.length === 0 && (
        <div className="text-center py-12">
          <Activity className="w-8 h-8 text-[#D1D5DB] mx-auto mb-2" />
          <p className="text-sm text-[#6B7280]">No usage data yet. Start using workspaces to see analytics.</p>
        </div>
      )}
    </div>
  );
}

// ---- Tab: Smart Routing + Fallback Chain (Phase 4.5 / 5.x) ----
function RoutingTab({ workspaceId }) {
  const [smartEnabled, setSmartEnabled] = useState(false);
  const [fallback, setFallback] = useState({ enabled: false, chain: [], triggers: [] });
  const [events, setEvents] = useState([]);
  const [costRules, setCostRules] = useState({ max_cost_per_call_usd: null, prefer_cheap_for_short_prompts: false, short_prompt_char_limit: null });
  const [region, setRegion] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!workspaceId) return;
    setLoading(true);
    Promise.all([
      routingAPI.getSmartRouting(workspaceId).then(r => r.data).catch(() => ({ enabled: false })),
      routingAPI.getFallback(workspaceId).then(r => r.data).catch(() => ({ enabled: false, chain: [], triggers: [] })),
      routingAPI.fallbackEvents(workspaceId, 7).then(r => r.data).catch(() => []),
      routingAPI.getCostRules(workspaceId).then(r => r.data).catch(() => ({ max_cost_per_call_usd: null, prefer_cheap_for_short_prompts: false, short_prompt_char_limit: null })),
      routingAPI.getRegion(workspaceId).then(r => r.data).catch(() => ({ region: '' })),
    ]).then(([sr, fb, ev, cr, rg]) => {
      setSmartEnabled(!!sr.enabled);
      setFallback({ enabled: !!fb.enabled, chain: fb.chain || [], triggers: fb.triggers || [] });
      setEvents(ev || []);
      setCostRules({ max_cost_per_call_usd: cr.max_cost_per_call_usd, prefer_cheap_for_short_prompts: !!cr.prefer_cheap_for_short_prompts, short_prompt_char_limit: cr.short_prompt_char_limit });
      setRegion(rg.region || '');
    }).finally(() => setLoading(false));
  }, [workspaceId]);

  const toggleSmart = async () => {
    const next = !smartEnabled;
    setSmartEnabled(next);
    try { await routingAPI.setSmartRouting(workspaceId, next); setMsg('Smart Routing ' + (next ? 'enabled' : 'disabled')); }
    catch (e) { setSmartEnabled(!next); setMsg('Failed: ' + (e.response?.data?.detail || e.message)); }
    setTimeout(() => setMsg(null), 2500);
  };

  const saveCostRules = async () => {
    setSaving(true);
    try {
      await routingAPI.setCostRules(workspaceId, costRules);
      setMsg('Cost rules saved');
    } catch (e) { setMsg('Failed: ' + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); setTimeout(() => setMsg(null), 2500); }
  };

  const saveRegion = async () => {
    setSaving(true);
    try {
      await routingAPI.setRegion(workspaceId, region.trim() || null);
      setMsg(region.trim() ? `Preferred region set: ${region.trim()}` : 'Preferred region cleared');
    } catch (e) { setMsg('Failed: ' + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); setTimeout(() => setMsg(null), 2500); }
  };

  const autoGenerate = async () => {
    setGenerating(true);
    try {
      const res = await routingAPI.autoGenerateChain(workspaceId);
      setFallback(prev => ({ ...prev, chain: res.data.chain || [] }));
      setMsg('Generated ' + (res.data.chain || []).length + '-step chain. Click Save to apply.');
    } catch (e) { setMsg('Failed: ' + (e.response?.data?.detail || e.message)); }
    finally { setGenerating(false); setTimeout(() => setMsg(null), 3500); }
  };

  const saveChain = async () => {
    setSaving(true);
    try {
      await routingAPI.setFallback(workspaceId, { enabled: fallback.enabled, chain: fallback.chain, triggers: fallback.triggers });
      setMsg('Fallback chain saved');
    } catch (e) { setMsg('Failed: ' + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); setTimeout(() => setMsg(null), 2500); }
  };

  const removeStep = (idx) => setFallback(f => ({ ...f, chain: f.chain.filter((_, i) => i !== idx) }));

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-[#6590FF] animate-spin" /></div>;

  return (
    <div className="p-5 space-y-5" data-testid="routing-tab">
      {msg && <div className="text-xs px-3 py-2 bg-[#F0F9FF] border border-[#BAE6FD] text-[#0369A1] rounded-xl">{msg}</div>}

      {/* Smart Routing toggle */}
      <div className="rounded-xl border border-[#E5E7EB] p-4 bg-white">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-[#6590FF]/10 flex items-center justify-center flex-shrink-0">
              <Wand2 className="w-4 h-4 text-[#6590FF]" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold text-[#111827]" style={{ fontFamily: 'var(--font-heading)' }}>Smart AI Routing</p>
              <p className="text-xs text-[#6B7280] mt-0.5">When on, BrainShift inspects each request and swaps your primary model when it lacks the required capability (vision, long context, reasoning, JSON mode). Picks the cheapest model that covers the need.</p>
            </div>
          </div>
          <button
            onClick={toggleSmart}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${smartEnabled ? 'bg-[#6590FF]' : 'bg-[#E5E7EB]'}`}
            data-testid="smart-routing-toggle"
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${smartEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>
      </div>

      {/* Cost optimisation rules */}
      <div className="rounded-xl border border-[#E5E7EB] p-4 bg-white" data-testid="cost-rules-section">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-9 h-9 rounded-xl bg-[#F59E0B]/10 flex items-center justify-center">
            <Target className="w-4 h-4 text-[#F59E0B]" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-[#111827]" style={{ fontFamily: 'var(--font-heading)' }}>Cost Optimisation Rules</p>
            <p className="text-xs text-[#6B7280] mt-0.5">Applied during Smart Routing. Caps spend per call and routes short, simple prompts to your cheapest model.</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-[11px] font-medium text-[#4B5563] mb-1 block">Max Cost / Call (USD)</label>
            <input type="number" step="0.001" min="0"
              value={costRules.max_cost_per_call_usd ?? ''}
              onChange={e => setCostRules(c => ({ ...c, max_cost_per_call_usd: e.target.value === '' ? null : parseFloat(e.target.value) }))}
              placeholder="e.g. 0.05"
              className="w-full h-9 px-3 text-xs border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#6590FF]"
              data-testid="max-cost-per-call-input" />
          </div>
          <div>
            <label className="text-[11px] font-medium text-[#4B5563] mb-1 block">Short-Prompt Limit (chars)</label>
            <input type="number" step="100" min="0"
              value={costRules.short_prompt_char_limit ?? ''}
              onChange={e => setCostRules(c => ({ ...c, short_prompt_char_limit: e.target.value === '' ? null : parseInt(e.target.value, 10) }))}
              placeholder="500"
              className="w-full h-9 px-3 text-xs border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#6590FF]"
              data-testid="short-prompt-limit-input" />
          </div>
        </div>
        <div className="flex items-center justify-between mb-3">
          <label className="flex items-center gap-2 text-xs text-[#4B5563] cursor-pointer">
            <input type="checkbox" checked={costRules.prefer_cheap_for_short_prompts}
              onChange={e => setCostRules(c => ({ ...c, prefer_cheap_for_short_prompts: e.target.checked }))}
              data-testid="prefer-cheap-cb" />
            Prefer cheapest model for short prompts
          </label>
          <button onClick={saveCostRules} disabled={saving}
            className="h-8 px-3 text-xs font-medium bg-[#111827] hover:bg-[#1F2937] text-white rounded-xl disabled:opacity-60"
            data-testid="save-cost-rules-btn">Save Rules</button>
        </div>
      </div>

      {/* Multi-region pinning */}
      <div className="rounded-xl border border-[#E5E7EB] p-4 bg-white" data-testid="region-section">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-9 h-9 rounded-xl bg-[#0891B2]/10 flex items-center justify-center">
            <Activity className="w-4 h-4 text-[#0891B2]" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-[#111827]" style={{ fontFamily: 'var(--font-heading)' }}>Preferred Provider Region</p>
            <p className="text-xs text-[#6B7280] mt-0.5">Pin requests to a region that matches your data-residency requirements. BYO credentials with this region tag are preferred when set.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <input type="text" value={region} onChange={e => setRegion(e.target.value)}
            placeholder="e.g. us-east-1, eu-west-2"
            className="flex-1 h-9 px-3 text-xs border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#6590FF]"
            data-testid="preferred-region-input" />
          <button onClick={saveRegion} disabled={saving}
            className="h-9 px-3 text-xs font-medium bg-[#111827] hover:bg-[#1F2937] text-white rounded-xl disabled:opacity-60"
            data-testid="save-region-btn">Save</button>
        </div>
      </div>

      {/* Fallback chain */}
      <div className="rounded-xl border border-[#E5E7EB] p-4 bg-white" data-testid="fallback-chain-section">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#059669]/10 flex items-center justify-center">
              <ShieldCheck className="w-4 h-4 text-[#059669]" />
            </div>
            <div>
              <p className="text-sm font-bold text-[#111827]" style={{ fontFamily: 'var(--font-heading)' }}>Fallback Chain</p>
              <p className="text-xs text-[#6B7280] mt-0.5">If the primary model times out, rate-limits, or returns a 5xx, BrainShift retries with the next model in this chain.</p>
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs text-[#4B5563] cursor-pointer">
            <input type="checkbox" checked={fallback.enabled} onChange={e => setFallback(f => ({ ...f, enabled: e.target.checked }))} data-testid="fallback-enabled-cb" />
            Enabled
          </label>
        </div>

        <div className="flex gap-2 mb-3">
          <button
            onClick={autoGenerate}
            disabled={generating}
            className="h-9 px-3 text-xs font-medium bg-[#6590FF] hover:bg-[#4B7CFF] text-white rounded-xl flex items-center gap-1.5 disabled:opacity-60"
            data-testid="auto-generate-chain-btn"
          >
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
            Auto-Generate
          </button>
          <button
            onClick={saveChain}
            disabled={saving}
            className="h-9 px-3 text-xs font-medium bg-[#111827] hover:bg-[#1F2937] text-white rounded-xl flex items-center gap-1.5 disabled:opacity-60"
            data-testid="save-chain-btn"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            Save
          </button>
        </div>

        {fallback.chain.length === 0 ? (
          <p className="text-xs text-[#9CA3AF] italic px-3 py-4 bg-[#F9FAFB] border border-dashed border-[#E5E7EB] rounded-xl">No fallback steps configured. Click Auto-Generate to create one from your available models.</p>
        ) : (
          <div className="space-y-1.5">
            {fallback.chain.map((step, i) => (
              <div key={`${step.provider}-${step.model}-${i}`} className="flex items-center gap-2 px-3 py-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-xs" data-testid={`fallback-step-${i}`}>
                <span className="w-5 h-5 rounded-full bg-[#6590FF]/10 text-[#6590FF] flex items-center justify-center font-bold text-[10px]">{i + 1}</span>
                <span className="font-medium text-[#111827]">{step.provider}</span>
                <ChevronRight className="w-3 h-3 text-[#9CA3AF]" />
                <span className="text-[#4B5563] truncate flex-1">{step.model}</span>
                <button onClick={() => removeStep(i)} className="text-[#9CA3AF] hover:text-[#DC2626]" data-testid={`remove-step-${i}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent fallback events */}
      <div className="rounded-xl border border-[#E5E7EB] p-4 bg-white" data-testid="fallback-events-section">
        <p className="text-sm font-bold text-[#111827] mb-2" style={{ fontFamily: 'var(--font-heading)' }}>Recent Fallback Events (7d)</p>
        {events.length === 0 ? (
          <p className="text-xs text-[#9CA3AF] italic">No fallback events — your primary model has been stable.</p>
        ) : (
          <div className="space-y-1.5 max-h-[220px] overflow-y-auto">
            {events.slice(0, 20).map(ev => (
              <div key={ev.id} className="flex items-center gap-2 px-2.5 py-1.5 bg-[#F9FAFB] rounded-lg text-[11px]">
                <span className="text-[#6B7280] w-[110px] truncate">{new Date(ev.date).toLocaleString()}</span>
                <span className="text-[#111827] font-medium">{ev.from}</span>
                <ChevronRight className="w-3 h-3 text-[#9CA3AF]" />
                <span className="text-[#111827] font-medium">{ev.to}</span>
                <span className="text-[#DC2626] ml-auto">{ev.reason}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Tab 2: Bring Your Own Model ----
function BYOMTab({ newConfig, setNewConfig, saving, onAdd }) {
  return (
    <div className="p-5">
      <div className="mb-5">
        <h3 className="text-sm font-bold text-[#111827] mb-1" style={{ fontFamily: 'var(--font-heading)' }}>Add a Custom Model</h3>
        <p className="text-xs text-[#6B7280]">Connect to any LLM provider: OCI GenAI, Azure OpenAI, AWS Bedrock, OpenRouter, or any OpenAI-compatible endpoint.</p>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-[#4B5563] mb-1.5 block">Display Name <span className="text-[#DC2626]">*</span></label>
            <input
              value={newConfig.name}
              onChange={e => setNewConfig({ ...newConfig, name: e.target.value })}
              placeholder="e.g., My Azure GPT-4"
              className="w-full h-10 px-3 text-sm border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#6590FF] focus:ring-1 focus:ring-[#6590FF]/20"
              data-testid="byom-name"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-[#4B5563] mb-1.5 block">Provider</label>
            <select
              value={newConfig.provider}
              onChange={e => setNewConfig({ ...newConfig, provider: e.target.value })}
              className="w-full h-10 px-3 text-sm border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#6590FF] focus:ring-1 focus:ring-[#6590FF]/20 bg-white"
              data-testid="byom-provider"
            >
              {PROVIDER_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="text-xs font-medium text-[#4B5563] mb-1.5 block">Model ID <span className="text-[#DC2626]">*</span></label>
          <input
            value={newConfig.model_name}
            onChange={e => setNewConfig({ ...newConfig, model_name: e.target.value })}
            placeholder="e.g., gpt-4o, cohere.command-r-plus, meta.llama-3.1-405b"
            className="w-full h-10 px-3 text-sm border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#6590FF] focus:ring-1 focus:ring-[#6590FF]/20"
            data-testid="byom-model-id"
          />
        </div>

        <div>
          <label className="text-xs font-medium text-[#4B5563] mb-1.5 block">Base URL / Endpoint</label>
          <input
            value={newConfig.base_url}
            onChange={e => setNewConfig({ ...newConfig, base_url: e.target.value })}
            placeholder="https://your-endpoint.com/v1"
            className="w-full h-10 px-3 text-sm border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#6590FF] focus:ring-1 focus:ring-[#6590FF]/20"
            data-testid="byom-base-url"
          />
          <p className="text-[10px] text-[#9CA3AF] mt-1">Optional. Required for Azure, OCI, or self-hosted models.</p>
        </div>

        <div>
          <label className="text-xs font-medium text-[#4B5563] mb-1.5 block">API Key</label>
          <input
            type="password"
            autoComplete="off"
            name="byom-api-key"
            data-1p-ignore="true"
            data-lpignore="true"
            value={newConfig.api_key}
            onChange={e => setNewConfig({ ...newConfig, api_key: e.target.value })}
            placeholder="sk-... or your provider API key"
            className="w-full h-10 px-3 text-sm border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#6590FF] focus:ring-1 focus:ring-[#6590FF]/20"
            data-testid="byom-api-key"
          />
          <p className="text-[10px] text-[#9CA3AF] mt-1">Optional. Uses the workspace default key if left blank.</p>
        </div>

        <Separator className="my-2" />

        <button
          onClick={onAdd}
          disabled={saving || !newConfig.name.trim() || !newConfig.model_name.trim()}
          className="w-full h-11 bg-[#6590FF] hover:bg-[#4B7CFF] text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="byom-save-btn"
        >
          {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Adding...</> : <><Plus className="w-4 h-4" /> Add Model</>}
        </button>
      </div>
    </div>
  );
}
